import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';

import { PlatformSocialService, VisibleSocialLink } from '../../core/services/platform-social.service';

/**
 * Official social icons. Renders nothing until at least one URL is configured.
 */
@Component({
  selector: 'app-social-links',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (social.visible().length) {
      <div>
        @if (heading()) {
          <h4 [class]="headingClass()">{{ heading() }}</h4>
        }
        <div class="flex flex-wrap items-center gap-2">
          @for (link of social.visible(); track link.key) {
            <a
              [href]="link.url"
              target="_blank"
              rel="noopener noreferrer"
              [attr.aria-label]="link.label"
              [class]="iconClass()"
            >
              <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor" aria-hidden="true">
                <path [attr.d]="path(link)" />
              </svg>
            </a>
          }
        </div>
      </div>
    }
  `,
})
export class SocialLinksComponent {
  readonly social = inject(PlatformSocialService);
  readonly heading = input('Follow Us');
  readonly headingClass = input('font-semibold text-sm mb-3 text-white');
  readonly iconClass = input(
    'inline-flex size-9 items-center justify-center rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white transition-colors'
  );

  path(link: VisibleSocialLink): string {
    switch (link.key) {
      case 'facebook':
        return 'M13.5 9H16V6h-2.5C11.57 6 10 7.57 10 9.5V11H8v3h2v8h3v-8h2.4l.6-3H13v-1.5c0-.28.22-.5.5-.5Z';
      case 'x':
        return 'M17.3 4H19.7l-5.77 6.6L20.5 20h-4.86l-3.8-4.97L7.4 20H5l6.17-7.05L4.2 4h4.97l3.44 4.57L17.3 4Zm-1.7 14.4h1.33L8.47 5.52H7.04L15.6 18.4Z';
      case 'linkedin':
        return 'M6.94 8.5H4.13V20h2.81V8.5ZM5.54 4C4.6 4 4 4.6 4 5.42c0 .8.58 1.42 1.5 1.42h.02c.96 0 1.54-.62 1.54-1.42C6.88 4.6 6.32 4 5.54 4ZM20 20h-2.8v-6.02c0-1.4-.5-2.36-1.75-2.36-.96 0-1.53.65-1.78 1.27-.1.23-.12.55-.12.87V20H10.75s.04-10.3 0-11.5h2.8v1.63c.37-.58 1.04-1.4 2.53-1.4 1.85 0 3.23 1.21 3.23 3.81V20Z';
      case 'instagram':
        return 'M8 4h8a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4Zm8 1.7H8A2.3 2.3 0 0 0 5.7 8v8A2.3 2.3 0 0 0 8 18.3h8A2.3 2.3 0 0 0 18.3 16V8A2.3 2.3 0 0 0 16 5.7ZM12 8.6A3.4 3.4 0 1 1 8.6 12 3.4 3.4 0 0 1 12 8.6Zm0 1.6A1.8 1.8 0 1 0 13.8 12 1.8 1.8 0 0 0 12 10.2Zm4.15-2.55a.85.85 0 1 1-.85.85.85.85 0 0 1 .85-.85Z';
      case 'youtube':
        return 'M21.6 7.2a2.7 2.7 0 0 0-1.9-1.9C18 5 12 5 12 5s-6 0-7.7.3a2.7 2.7 0 0 0-1.9 1.9A28 28 0 0 0 2 12a28 28 0 0 0 .4 4.8 2.7 2.7 0 0 0 1.9 1.9C6 19 12 19 12 19s6 0 7.7-.3a2.7 2.7 0 0 0 1.9-1.9A28 28 0 0 0 22 12a28 28 0 0 0-.4-4.8ZM10.2 15.2V8.8L15.5 12l-5.3 3.2Z';
      case 'whatsapp':
        return 'M12 3.2A8.8 8.8 0 0 0 4.4 16.4L3.2 21l4.7-1.2A8.8 8.8 0 1 0 12 3.2Zm0 16.1a7.3 7.3 0 0 1-3.72-1.02l-.27-.16-2.78.73.74-2.71-.17-.28A7.3 7.3 0 1 1 12 19.3Zm4.02-5.47c-.22-.11-1.3-.64-1.5-.71-.2-.08-.35-.11-.5.11-.15.22-.57.71-.7.86-.13.14-.26.16-.48.05-.22-.11-.93-.34-1.77-1.1-.65-.58-1.1-1.3-1.22-1.52-.13-.22-.01-.34.1-.45.1-.1.22-.26.33-.39.11-.13.15-.22.22-.37.08-.15.04-.28-.02-.39-.05-.11-.5-1.2-.68-1.64-.18-.43-.36-.37-.5-.38h-.42c-.15 0-.39.05-.6.28-.2.22-.79.77-.79 1.88 0 1.1.81 2.17.92 2.32.11.15 1.6 2.44 3.87 3.42.54.23.96.37 1.29.48.54.17 1.04.15 1.43.09.44-.06 1.3-.53 1.48-1.05.18-.51.18-.96.13-1.05-.05-.1-.2-.15-.42-.26Z';
      case 'tiktok':
        return 'M14.6 4c.4 1.7 1.5 3.1 3.1 3.7V10c-1.1.1-2.2-.2-3.1-.7v5.4c0 3-2.4 4.9-5.1 4.9S4.3 17.7 4.3 14.8c0-2.9 2.5-4.8 5.2-4.8.3 0 .6 0 .9.1v2.7c-.3-.1-.6-.2-.9-.2-1.5 0-2.7 1.1-2.7 2.3 0 1.3 1.2 2.3 2.7 2.3 1.5 0 2.6-1 2.6-2.5V4h2.5Z';
      default:
        return '';
    }
  }
}
